/**
 * Answer matching for quiz questions (frontend copy of
 * `backend/src/services/answerMatching.ts`; keep the two in step).
 *
 * The frontend submits the letter of the chosen option (A, B, C, ...) when
 * `options_json` is an array, while seeded answer keys are either that letter
 * or the option text. Both sides are resolved to an option index before
 * comparing, so either key form matches either submission form.
 */

export type QuestionOptions = string[] | Record<string, string> | null | undefined;

/** Trims and lower-cases a value so comparisons ignore case and padding. */
const normalize = (value: unknown): string => String(value ?? '').trim().toLowerCase();

/**
 * Resolves an answer to an index in an array of options, or -1 when it names
 * no option. `prefer` settles an answer that is both a letter and the text of
 * an option (options such as ["B", "A", "D", "C"]).
 */
function resolveOptionIndex(answer: string, options: string[], prefer: 'letter' | 'text'): number {
  const byText = options.findIndex(option => normalize(option) === answer);
  const letterIndex = /^[a-z]$/.test(answer) ? answer.charCodeAt(0) - 'a'.charCodeAt(0) : -1;
  const byLetter = letterIndex < options.length ? letterIndex : -1;

  const [first, second] = prefer === 'letter' ? [byLetter, byText] : [byText, byLetter];
  return first !== -1 ? first : second;
}

/**
 * Decides whether a submitted answer matches a question's answer key.
 *
 * @param userAnswer - the submission: an option letter, option text, or free text
 * @param answerKey - the stored key: an option letter or option text
 * @param options - the question's options, as an array or a letter-keyed object
 */
export function isAnswerCorrect(userAnswer: string, answerKey: string, options?: QuestionOptions): boolean {
  const submitted = normalize(userAnswer);
  const key = normalize(answerKey);
  if (!submitted || !key) return false;

  // An empty list offers nothing to choose, so it is free text like no options (#130).
  if (Array.isArray(options) && options.length > 0) {
    // The frontend submits option letters; seeded keys are mostly option text.
    const keyIndex = resolveOptionIndex(key, options, 'text');
    return keyIndex !== -1 && resolveOptionIndex(submitted, options, 'letter') === keyIndex;
  }

  if (options && !Array.isArray(options) && typeof options === 'object') {
    const keyText = normalize(options[answerKey.trim()] ?? options[answerKey.trim().toUpperCase()]);
    return submitted === key || (keyText !== '' && submitted === keyText);
  }

  return submitted === key;
}
